import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { updateArea, fetchAreaMembers, addAreaMembers, removeAreaMember, fetchOrgMembers } from '@/lib/api';
import { useOrgStore } from '@/stores/org.store';
import { useStatusStore, isCurrentUserUnavailable } from '@/stores/status.store';
import { useAuthStore } from '@/stores/auth.store';
import { IconPicker } from '@/components/ui/icon-picker';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { toast } from '@/stores/toast.store';

const PRESET_COLORS = [
  '#EF4444', '#F97316', '#F59E0B', '#84CC16',
  '#22C55E', '#14B8A6', '#06B6D4', '#3B82F6',
  '#6366F1', '#8B5CF6', '#EC4899', '#F43F5E',
];

interface AreaMember {
  userId: string;
  fullName: string;
  displayName: string;
  profilePictureUrl: string | null;
  role: string;
  isExternal: boolean;
  joinedAt: string;
}

interface Props {
  orgId: string;
  areaId: string;
  onClose: () => void;
}

export function AreaSettingsPanel({ orgId, areaId, onClose }: Props) {
  const areas = useOrgStore((s) => s.areas);
  const area = areas.find((a) => a.id === areaId);
  const updateAreaInStore = useOrgStore((s) => s.updateArea);
  const availabilityByUserId = useStatusStore((s) => s.availabilityByUserId);
  const currentUserId = useAuthStore((s) => s.user?.id);
  const myNotificationSettings = useStatusStore((s) => s.myNotificationSettings);
  const panelRef = useFocusTrap<HTMLDivElement>(true);

  const [activeTab, setActiveTab] = useState<'general' | 'members'>('general');

  // General tab state
  const [name, setName] = useState(area?.name ?? '');
  const [icon, setIcon] = useState(area?.icon ?? null);
  const [iconColor, setIconColor] = useState(area?.iconColor ?? null);
  const [description, setDescription] = useState(area?.description ?? '');
  const [visibility, setVisibility] = useState(area?.visibility ?? 'public');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Members tab state
  const [members, setMembers] = useState<AreaMember[]>([]);
  const [orgMembers, setOrgMembers] = useState<Array<{ userId: string; displayName: string; profilePictureUrl: string | null }>>([]);
  const [addSearch, setAddSearch] = useState('');
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Load members when tab switches
  useEffect(() => {
    if (activeTab !== 'members') return;
    setLoadingMembers(true);
    Promise.all([
      fetchAreaMembers(orgId, areaId),
      fetchOrgMembers(orgId),
    ])
      .then(([areaData, orgData]) => {
        setMembers(areaData.members);
        setOrgMembers(orgData.members.map((m: Record<string, unknown>) => ({
          userId: m.userId as string,
          displayName: m.displayName as string,
          profilePictureUrl: m.profilePictureUrl as string | null,
        })));
      })
      .catch(() => toast.error('Failed to load members'))
      .finally(() => setLoadingMembers(false));
  }, [activeTab, orgId, areaId]);

  // Sync if area changes
  useEffect(() => {
    if (area) {
      setName(area.name);
      setIcon(area.icon);
      setIconColor(area.iconColor);
      setDescription(area.description ?? '');
      setVisibility(area.visibility);
    }
  }, [area]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const updates: Record<string, unknown> = {};
      if (name !== area?.name) updates.name = name;
      if (icon !== area?.icon) updates.icon = icon;
      if (iconColor !== area?.iconColor) updates.iconColor = iconColor;
      if (description !== (area?.description ?? '')) updates.description = description || null;
      if (visibility !== area?.visibility) updates.visibility = visibility;

      if (Object.keys(updates).length > 0) {
        const result = await updateArea(orgId, areaId, updates);
        const updatedArea = result.area as Record<string, string | null>;
        updateAreaInStore(areaId, {
          name: updatedArea.name as string ?? area?.name,
          icon: updatedArea.icon ?? null,
          iconColor: updatedArea.iconColor ?? null,
          description: updatedArea.description ?? null,
          visibility: updatedArea.visibility as string ?? area?.visibility,
        });
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      toast.success('Settings saved');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  }, [orgId, areaId, name, icon, iconColor, description, visibility, area, updateAreaInStore]);

  const handleAddMember = useCallback(async (userId: string) => {
    try {
      await addAreaMembers(orgId, areaId, [userId]);
      // Refresh members
      const data = await fetchAreaMembers(orgId, areaId);
      setMembers(data.members);
      setAddSearch('');
      toast.success('Member added');
    } catch {
      toast.error('Failed to add member');
    }
  }, [orgId, areaId]);

  const handleRemoveMember = useCallback(async (userId: string) => {
    try {
      await removeAreaMember(orgId, areaId, userId);
      setMembers((prev) => prev.filter((m) => m.userId !== userId));
      toast.success('Member removed');
    } catch {
      toast.error('Failed to remove member');
    }
  }, [orgId, areaId]);

  const memberIds = useMemo(() => new Set(members.map((m) => m.userId)), [members]);
  const filteredOrgMembers = useMemo(
    () => orgMembers.filter(
      (m) => !memberIds.has(m.userId) && m.displayName.toLowerCase().includes(addSearch.toLowerCase()),
    ),
    [members, orgMembers, addSearch],
  );

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
    }
  }, [onClose]);

  return (
    <>
      {/* Backdrop on mobile */}
      <div className="fixed inset-0 z-40 bg-black/40 sm:hidden" onClick={onClose} onKeyDown={handleKeyDown} />
      <div ref={panelRef} role="dialog" aria-modal="true" aria-label="Room settings" onKeyDown={handleKeyDown} className="fixed inset-y-0 right-0 z-50 flex w-full flex-col bg-white sm:relative sm:inset-auto sm:z-auto sm:w-96 sm:flex-shrink-0 sm:border-l sm:border-gray-200 dark:bg-gray-900 sm:dark:border-gray-800">
      {/* Header */}
      <div className="flex h-12 flex-shrink-0 items-center justify-between border-b border-gray-200 px-3 sm:h-14 sm:px-4 dark:border-gray-800">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Room settings</h2>
        <button
          onClick={onClose}
          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-gray-200 dark:border-gray-800">
        <button
          onClick={() => setActiveTab('general')}
          className={`flex-1 border-b-2 px-4 py-2 text-xs font-medium transition-colors ${
            activeTab === 'general'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
          }`}
        >
          General
        </button>
        <button
          onClick={() => setActiveTab('members')}
          className={`flex-1 border-b-2 px-4 py-2 text-xs font-medium transition-colors ${
            activeTab === 'members'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
          }`}
        >
          Members ({members.length || '...'})
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3 sm:p-4">
        {activeTab === 'general' ? (
          <div className="space-y-5">
            {/* Name */}
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              />
            </div>

            {/* Icon */}
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Icon</label>
              <IconPicker value={icon} onChange={setIcon} />
            </div>

            {/* Icon Color */}
            <div>
              <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300">Icon Color</label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setIconColor(null)}
                  className={`flex h-7 w-7 items-center justify-center rounded-full border-2 transition-colors ${
                    iconColor === null ? 'border-blue-500' : 'border-gray-200 dark:border-gray-700'
                  }`}
                  title="Default"
                >
                  <svg className="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                </button>
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color}
                    onClick={() => setIconColor(color)}
                    className={`h-7 w-7 rounded-full border-2 transition-all outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
                      iconColor === color ? 'border-blue-500 scale-110' : 'border-transparent hover:scale-105'
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="What is this room about?"
                className="w-full resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none placeholder:text-gray-400 focus:border-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              />
            </div>

            {/* Visibility */}
            <div>
              <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300">Visibility</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setVisibility('public')}
                  className={`flex-1 rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${
                    visibility === 'public'
                      ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800'
                  }`}
                >
                  Public
                </button>
                <button
                  onClick={() => setVisibility('private')}
                  className={`flex-1 rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${
                    visibility === 'private'
                      ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800'
                  }`}
                >
                  Private
                </button>
              </div>
            </div>

            {/* Save button */}
            <button
              onClick={handleSave}
              disabled={saving || !name.trim()}
              className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Add member search */}
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Add member</label>
              <input
                type="text"
                value={addSearch}
                onChange={(e) => setAddSearch(e.target.value)}
                placeholder="Search org members..."
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none placeholder:text-gray-400 focus:border-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              />
              {addSearch && filteredOrgMembers.length > 0 && (
                <div className="mt-1 max-h-32 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
                  {filteredOrgMembers.slice(0, 8).map((m) => (
                    <button
                      key={m.userId}
                      onClick={() => handleAddMember(m.userId)}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-medium text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                        {m.profilePictureUrl ? (
                          <img src={m.profilePictureUrl} alt="" className="h-6 w-6 rounded-full object-cover" />
                        ) : (
                          m.displayName.charAt(0).toUpperCase()
                        )}
                      </div>
                      <span className="truncate text-gray-700 dark:text-gray-300">{m.displayName}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Member list */}
            {loadingMembers ? (
              <p className="text-center text-sm text-gray-500">Loading...</p>
            ) : (
              <div className="space-y-1">
                {members.map((m) => {
                  const isMemberUnavailable = m.userId === currentUserId
                    ? isCurrentUserUnavailable(myNotificationSettings)
                    : !!(availabilityByUserId[m.userId]?.isDnd || availabilityByUserId[m.userId]?.isOutsideWorkingHours);
                  return (
                  <div key={m.userId} className="flex items-center gap-2 rounded-lg px-2 py-1.5">
                    <div className="relative flex-shrink-0">
                      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-100 text-xs font-medium text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                        {m.profilePictureUrl ? (
                          <img src={m.profilePictureUrl} alt="" className="h-7 w-7 rounded-full object-cover" />
                        ) : (
                          m.displayName.charAt(0).toUpperCase()
                        )}
                      </div>
                      {isMemberUnavailable && (
                        <span className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-white text-[8px] leading-none dark:bg-gray-900" title="Do Not Disturb / Outside working hours">🌙</span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-gray-800 dark:text-gray-200">{m.displayName}</span>
                      {m.role !== 'member' && (
                        <span className="text-[10px] font-medium uppercase text-gray-400">{m.role}</span>
                      )}
                    </div>
                    {m.isExternal && (
                      <span className="rounded bg-gray-200 px-1 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        External
                      </span>
                    )}
                    <button
                      onClick={() => handleRemoveMember(m.userId)}
                      className="rounded p-1 text-gray-400 opacity-0 transition-opacity hover:text-red-500 group-hover:opacity-100 [div:hover>&]:opacity-100"
                      title="Remove member"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    </>
  );
}
